import { LitElement } from "lit";

/**
 * html2canvas / html2pdf clones the live document into an iframe.
 * Lit then reconnects custom elements there and tries to adopt the parent
 * document's constructed stylesheets — browsers throw NotAllowedError.
 */

const wrappedConstructors = new WeakSet<CustomElementConstructor>();

export function isForeignDocumentClone(node: Node): boolean {
  return typeof document !== "undefined" && node.ownerDocument !== document;
}

export function ignoreHtml2CanvasCustomElement(
  el: Element,
  allowTags: readonly string[] = [],
): boolean {
  const tag = el.tagName;
  if (!tag.includes("-")) return false;
  return !allowTags.includes(tag);
}

function isSharedStylesheetError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    error.name === "NotAllowedError" &&
    /adoptedStyleSheets|multiple documents/i.test(error.message)
  );
}

function wrapConnectedCallback(ctor: CustomElementConstructor): void {
  if (wrappedConstructors.has(ctor)) return;
  wrappedConstructors.add(ctor);
  const proto = ctor.prototype as {
    connectedCallback?: () => void;
  };
  const original = proto.connectedCallback;
  proto.connectedCallback = function (this: HTMLElement) {
    if (isForeignDocumentClone(this)) return;
    original?.call(this);
  };
}

let installed = false;

export function installLitIframeCloneGuard(): void {
  if (installed || typeof customElements === "undefined") return;
  installed = true;

  type RenderRootHost = {
    createRenderRoot(): HTMLElement | DocumentFragment;
  };
  const proto = LitElement.prototype as unknown as RenderRootHost;
  const originalCreateRoot = proto.createRenderRoot;
  proto.createRenderRoot = function (this: LitElement) {
    if (isForeignDocumentClone(this)) {
      return this.attachShadow({ mode: "open" });
    }
    try {
      return originalCreateRoot.call(this);
    } catch (error) {
      if (isSharedStylesheetError(error)) {
        return this.attachShadow({ mode: "open" });
      }
      throw error;
    }
  };

  const originalDefine = customElements.define.bind(customElements);
  customElements.define = ((
    name: string,
    ctor: CustomElementConstructor,
    options?: ElementDefinitionOptions,
  ) => {
    wrapConnectedCallback(ctor);
    return originalDefine(name, ctor, options);
  }) as typeof customElements.define;

  for (const name of ["message-modal"]) {
    const ctor = customElements.get(name);
    if (ctor) wrapConnectedCallback(ctor);
  }
}

